import React, { createContext, PropsWithChildren, useContext } from 'react';
import { SwiftFoxEvent, SwiftFoxEventResult, SwiftFoxRsvpOption } from '../events/contracts';

type EventListContextValue = {
	displayMode: 'compact' | 'list';
	setDisplayMode: (displayMode: 'compact' | 'list') => void;
	search: string;
	setSearch: React.Dispatch<React.SetStateAction<string>>;
	setStartDate: React.Dispatch<React.SetStateAction<string>>;
	setEndDate: (startDate: string) => void;
	locationFilter: string[];
	setLocationFilter: React.Dispatch<React.SetStateAction<string[]>>;
	stateFilter: string[];
	setStateFilter: React.Dispatch<React.SetStateAction<string[]>>;
	rsvpFilter: string[];
	setRsvpFilter: React.Dispatch<React.SetStateAction<string[]>>;
	dateFilter: string;
	setDateFilter: React.Dispatch<React.SetStateAction<string>>;
	rsvpOptions: SwiftFoxRsvpOption[];
	setSelectedPage: React.Dispatch<React.SetStateAction<number>>;
	total: number;
	events: SwiftFoxEvent[];
	defaultThumbnailUrl: string;
	hideThumbnail: boolean;
	refetch: () => void;
};

const EventListContext = createContext<EventListContextValue | undefined>(undefined);

type EventListContextProviderProps = {
	rsvpOptions: SwiftFoxRsvpOption[];
	displayMode?: 'compact' | 'list';
	setDisplayMode?: (displayMode: 'compact' | 'list') => void;
	search?: string;
	setSearch?: React.Dispatch<React.SetStateAction<string>>;
	setStartDate?: React.Dispatch<React.SetStateAction<string>>;
	setEndDate?: (startDate: string) => void;
	locationFilter?: string[];
	setLocationFilter?: React.Dispatch<React.SetStateAction<string[]>>;
	stateFilter?: string[];
	setStateFilter?: React.Dispatch<React.SetStateAction<string[]>>;
	rsvpFilter?: string[];
	setRsvpFilter?: React.Dispatch<React.SetStateAction<string[]>>;
	dateFilter?: string;
	setDateFilter?: React.Dispatch<React.SetStateAction<string>>;
	setSelectedPage?: React.Dispatch<React.SetStateAction<number>>;
	eventResult?: SwiftFoxEventResult;
	defaultThumbnailUrl?: string;
	hideThumbnail?: boolean;
	refetch?: () => void;
	children: React.ReactNode;
};

export const EventListContextProvider: React.FC<EventListContextProviderProps> = props => {
	const {
		dateFilter,
		displayMode,
		eventResult,
		locationFilter,
		refetch,
		rsvpFilter,
		rsvpOptions,
		search,
		setDateFilter,
		setDisplayMode,
		setEndDate,
		setLocationFilter,
		setRsvpFilter,
		setSearch,
		setSelectedPage,
		setStartDate,
		defaultThumbnailUrl,
		hideThumbnail,
		setStateFilter,
		stateFilter
	} = props;
	return (
		<EventListContext.Provider
			value={{
				dateFilter,
				defaultThumbnailUrl,
				displayMode,
				events: eventResult?.events,
				hideThumbnail,
				locationFilter,
				refetch,
				rsvpFilter,
				rsvpOptions,
				search,
				setDateFilter,
				setDisplayMode,
				setEndDate,
				setLocationFilter,
				setRsvpFilter,
				setSearch,
				setSelectedPage,
				setStartDate,
				setStateFilter,
				stateFilter,
				total: eventResult?.total
			}}
		>
			{props.children}
		</EventListContext.Provider>
	);
};

export const useEventListContext = () => useContext(EventListContext);
